// Die Unterlage für den Monteur und die Liste der Unterlagen (E3, S4; H17;
// PLAN.md, E3, „Handwerk“: „zuerst als Druckansicht im Browser“) – reine
// Logik, ohne React: was auf dem Blatt steht und in welcher Ordnung.
//
// Die Unterlage ist die Besichtigung ganz (GET /api/crm/aufnahme/:id) – hier
// wird sie fürs Papier geordnet: je Station die Fotos (in der Reihenfolge der
// Aufnahme) mit ihren Notizen, die Notizen zur Station, die Längen je Art,
// „Sonstiges“; offen gebliebene Stationen deutlich; am Ende das Material,
// das jetzt gilt. Erfunden wird nichts: Was fehlt, steht als „fehlt“ da.
//
// Seit E3b (O65): je Station auch das Material, das der Verkäufer draußen
// angegeben hat (wie die Längen), und was er am Schluss angab; Menge 0
// („doch nicht gebraucht“) bleibt weg.

import { LAENGEN_ARTEN, nurStellenImCode, STATIONEN, type CrmAufnahme, type Montagetermin, type PositionAmServer, type UnterlagenZeile } from "@happy-energy/kern";
import { kalendertagZeigen } from "../anzeige.ts";

// --- Montagetermin und Liste ---------------------------------------------------------------------

/** „Di, 4. Nov. 2026 · Partie 2, 3 Tage“ – ohne Termin: „noch nicht vereinbart“. */
export function montageterminText(t: Montagetermin): string {
  const teile = [t.am ? kalendertagZeigen(t.am) : null, t.text?.trim() || null].filter((x): x is string => x !== null);
  return teile.length > 0 ? teile.join(" · ") : "noch nicht vereinbart";
}

export interface UnterlagenGruppe {
  titel: string;
  zeilen: UnterlagenZeile[];
}

/**
 * Die Liste in drei Abschnitten, wie der Server sie ordnet (kommende Termine,
 * der nächste zuerst – dann ohne Termin – zuletzt vergangene): Die Abschnitte
 * bekommen nur eine Überschrift, die Reihenfolge bleibt die des Servers.
 * Leere Abschnitte fallen weg.
 */
export function unterlagenGruppen(zeilen: readonly UnterlagenZeile[], heute: string): UnterlagenGruppe[] {
  const kommend = zeilen.filter((z) => z.montagetermin.am !== null && z.montagetermin.am >= heute);
  const ohne = zeilen.filter((z) => z.montagetermin.am === null);
  const vorbei = zeilen.filter((z) => z.montagetermin.am !== null && z.montagetermin.am < heute);
  return [
    { titel: "Kommende Montagen – die nächste zuerst", zeilen: kommend },
    { titel: "Noch ohne Montagetermin", zeilen: ohne },
    { titel: "Montagetermin vorbei", zeilen: vorbei },
  ].filter((g) => g.zeilen.length > 0);
}

// --- Das Blatt -------------------------------------------------------------------------------------

type Foto = CrmAufnahme["fotos"][number];
type Notiz = CrmAufnahme["notizen"][number];

export interface FotoAufDemBlatt {
  foto: Foto;
  /** Die Notizen zum Foto, getippt – leere weggelassen. */
  notizen: string[];
  /** Sprachmemos zum Foto – auf Papier nur der Hinweis, wie viele. */
  memos: number;
}

export interface LaengeAufDemBlatt {
  name: string;
  einheit: string;
  /** Die einzelnen Längen, wie gemessen, mit Anmerkung. */
  werte: { meter: number; anmerkung: string | null }[];
  summe: number;
}

/** Material von draußen auf dem Blatt (seit E3b): „Solarkabel 6 mm² · 25 m“. */
export interface MaterialAufDemBlatt {
  bezeichnung: string;
  artikelnummer: string | null;
  menge: number;
  einheit: string;
}

export interface StationAufDemBlatt {
  id: string;
  nr: number;
  name: string;
  fertig: boolean;
  fotos: FotoAufDemBlatt[];
  /** Die Notizen zur Station – getippt; eine, die nur eingesprochen ist, mit Hinweis. */
  notizen: { text: string | null; memos: number }[];
  laengen: LaengeAufDemBlatt[];
  /** Seit E3b: das Material von draußen an dieser Station. */
  material: MaterialAufDemBlatt[];
  sonstiges: string | null;
}

/** Das Material von draußen an einer Station (oder mit null: am Schluss) – ohne Menge 0, in der Reihenfolge des Servers. */
export function draussenAufDemBlatt(a: Partial<Pick<CrmAufnahme, "material">>, stationId: string | null): MaterialAufDemBlatt[] {
  return (a.material ?? [])
    .filter((m) => m.stationId === stationId && m.menge > 0)
    .map((m) => ({ bezeichnung: m.bezeichnung, artikelnummer: m.artikelnummer, menge: m.menge, einheit: m.einheit }));
}

function memosZu(a: Pick<CrmAufnahme, "memos">, notizId: string): number {
  return a.memos.filter((m) => m.notizId === notizId).length;
}

/** Die Stationen in ihrer Nummer, je mit allem, was aufs Blatt gehört. */
export function stationenAufDemBlatt(a: Pick<CrmAufnahme, "stationen" | "fotos" | "notizen" | "memos" | "laengen"> & Partial<Pick<CrmAufnahme, "material">>): StationAufDemBlatt[] {
  const notizenZuFoto = (fotoId: string): Notiz[] => a.notizen.filter((n) => n.fotoId === fotoId);
  return [...a.stationen]
    .sort((x, y) => x.nr - y.nr)
    .map((s) => {
      const fotos = a.fotos
        .filter((f) => f.stationId === s.id)
        .sort((x, y) => x.reihenfolge - y.reihenfolge)
        .map((foto) => {
          const notizen = notizenZuFoto(foto.id);
          return {
            foto,
            notizen: notizen.map((n) => n.text.trim()).filter(Boolean),
            memos: notizen.reduce((summe, n) => summe + memosZu(a, n.id), 0),
          };
        });
      const notizen = a.notizen
        .filter((n) => n.stationId === s.id && n.fotoId === null)
        .map((n) => ({ text: n.text.trim() || null, memos: memosZu(a, n.id) }))
        .filter((n) => n.text !== null || n.memos > 0);
      const laengen = LAENGEN_ARTEN.flatMap((art): LaengeAufDemBlatt[] => {
        const werte = a.laengen.filter((l) => l.stationId === s.id && l.laengenArt === art.schluessel).map((l) => ({ meter: l.meter, anmerkung: l.anmerkung }));
        if (werte.length === 0) return [];
        // Auf zwei Stellen, wie gemessen – 0,1 + 0,2 soll 0,3 bleiben.
        const summe = Math.round(werte.reduce((x, w) => x + w.meter, 0) * 100) / 100;
        return [{ name: art.name, einheit: art.einheit, werte, summe }];
      });
      return {
        id: s.id,
        nr: s.nr,
        name: STATIONEN.find((x) => x.schluessel === s.art)?.name ?? s.art,
        fertig: s.fertig,
        fotos,
        notizen,
        laengen,
        material: draussenAufDemBlatt(a, s.id),
        sonstiges: s.sonstiges?.trim() || null,
      };
    });
}

/** Wie viele Fotos nebeneinander: zwei bei bis zu zwei und bei vier (2 × 2), sonst drei – so bleibt keine Zeile fast leer. */
export function fotosJeZeile(anzahl: number): 2 | 3 {
  return anzahl <= 2 || anzahl === 4 ? 2 : 3;
}

/**
 * Das Material auf dem Blatt: was jetzt gilt (Reihenfolge der Liste), und
 * wann es zuletzt geändert wurde – null, wenn seit der Freigabe nichts.
 */
export function materialAufDemBlatt(positionen: readonly PositionAmServer[], freigegebenAm: string | null): { geltende: PositionAmServer[]; geaendertAm: string | null } {
  const geltende = positionen.filter((p) => p.ersetztAm === null);
  if (freigegebenAm === null) return { geltende, geaendertAm: null };
  let juengste = Date.parse(freigegebenAm);
  let text: string | null = null;
  for (const p of positionen) {
    for (const zeit of [p.angelegtAm, p.ersetztAm]) {
      if (zeit !== null && Date.parse(zeit) > juengste) {
        juengste = Date.parse(zeit);
        text = zeit;
      }
    }
  }
  return { geltende, geaendertAm: text };
}

// --- „PDF herunterladen“: was die Seite danach sagt (Nachprüfung 2, 23.10.2026) --------------------

/**
 * Was die Seite nach dem Bauen sagt – fehlen Fotos, als Warnung, nicht als
 * Erfolg (Nachprüfung 2, Befund 2): Sonst schickt das Büro die Unterlage
 * weiter und merkt erst in der Montage, dass Fotos fehlen.
 */
export function pdfFertigMeldung(dateiname: string, fotosFehlen: number): { text: string; art: "ok" | "fehler" } {
  if (fotosFehlen === 0) return { text: `PDF fertig – „${dateiname}“`, art: "ok" };
  const fehlen = fotosFehlen === 1 ? "1 Foto fehlt" : `${fotosFehlen} Fotos fehlen`;
  return { text: `PDF fertig – ${fehlen} darin, bitte neu bauen.`, art: "fehler" };
}

/**
 * Scheitert das Bauen ganz, erfährt es die Fehlerwache – ohne Kundendaten:
 * die Meldung ist fest, mit geht nur die Art des Fehlers und die Stellen im
 * Code (die erste Zeile des Stapels trägt die Meldung des Fehlers und bleibt
 * weg; seit dem Gutachten vom 3.11.2026, Befund 4, nach der einen Regel des
 * Kerns, nurStellenImCode). Die Adresse der Seite geht mit; ihre Kennungen
 * nimmt die Fehlerwache heraus (kern/fehlerwache.ts, `ohneKennungen`).
 */
export function pdfFehlerFuerWache(fehler: unknown): Error {
  const f = fehler instanceof Error ? fehler : new Error("unbekannt");
  const meldung = new Error("PDF der Unterlage ließ sich nicht bauen");
  meldung.name = f.name.slice(0, 200);
  const stellen = nurStellenImCode(f.stack, f);
  meldung.stack = [`${meldung.name}: ${meldung.message}`, ...(stellen ? [stellen] : [])].join("\n");
  return meldung;
}
